/**
 * useMedicationReminders Hook
 *
 * Provides medication reminder management with realtime sync,
 * CRUD operations, pause/resume, and automatic data refresh.
 * Includes pending reminder logs (sent but not yet acknowledged by the user).
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { isValidUUID } from '@/lib/utils';
import {
  MedicationReminder,
  CreateMedicationReminder,
  UpdateMedicationReminder,
  PendingReminderLog,
  getMedicationReminders,
  createMedicationReminder,
  updateMedicationReminder,
  toggleMedicationReminderPause,
  deleteMedicationReminder,
  getPendingReminderLogs,
  acknowledgeReminderLog,
} from '@/services/medicationReminderService';

export interface UseMedicationRemindersReturn {
  reminders: MedicationReminder[];
  activeReminders: MedicationReminder[];
  pausedReminders: MedicationReminder[];
  pendingLogs: PendingReminderLog[];
  isLoading: boolean;
  isCreating: boolean;
  addReminder: (reminder: CreateMedicationReminder) => Promise<MedicationReminder | null>;
  editReminder: (id: string, updates: UpdateMedicationReminder) => Promise<MedicationReminder | null>;
  togglePause: (id: string, isPaused: boolean) => Promise<boolean>;
  removeReminder: (id: string) => Promise<boolean>;
  acknowledgeLog: (logId: string, wasGiven: boolean, givenAt?: string) => Promise<boolean>;
  refresh: () => Promise<void>;
}

export function useMedicationReminders(): UseMedicationRemindersReturn {
  const { user } = useAuth();
  const { lastCompletedPregnancy } = usePregnancyStore();

  const pregnancyId = lastCompletedPregnancy?.id;
  const validPregnancyId = isValidUUID(pregnancyId) ? pregnancyId : null;

  const [reminders, setReminders] = useState<MedicationReminder[]>([]);
  const [pendingLogs, setPendingLogs] = useState<PendingReminderLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);

  // Fetch reminders and pending logs
  const fetchReminders = useCallback(async () => {
    if (!validPregnancyId) {
      setReminders([]);
      setPendingLogs([]);
      setIsLoading(false);
      return;
    }

    const [remindersResult, logsResult] = await Promise.all([
      getMedicationReminders(validPregnancyId),
      getPendingReminderLogs(validPregnancyId),
    ]);

    if (!remindersResult.error) {
      setReminders(remindersResult.data);
    }
    if (!logsResult.error) {
      setPendingLogs(logsResult.data);
    }
    setIsLoading(false);
  }, [validPregnancyId]);

  // Initial load
  useEffect(() => {
    fetchReminders();
  }, [fetchReminders]);

  // Realtime subscription for reminders + logs
  useEffect(() => {
    if (!validPregnancyId) return;

    const channel = supabase
      .channel('medication_reminders_and_logs')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'medication_reminders',
          filter: `pregnancy_id=eq.${validPregnancyId}`,
        },
        () => {
          fetchReminders();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'medication_reminder_log',
          filter: `pregnancy_id=eq.${validPregnancyId}`,
        },
        () => {
          fetchReminders();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [validPregnancyId, fetchReminders]);

  // Add reminder
  const addReminder = useCallback(
    async (reminder: CreateMedicationReminder): Promise<MedicationReminder | null> => {
      if (!user?.id || !validPregnancyId) return null;

      setIsCreating(true);
      const { data, error } = await createMedicationReminder(user.id, {
        ...reminder,
        pregnancy_id: validPregnancyId,
      });
      setIsCreating(false);

      if (error || !data) return null;

      // Optimistic update
      setReminders((prev) => [data, ...prev]);
      return data;
    },
    [user?.id, validPregnancyId]
  );

  // Edit reminder
  const editReminder = useCallback(
    async (id: string, updates: UpdateMedicationReminder): Promise<MedicationReminder | null> => {
      const { data, error } = await updateMedicationReminder(id, updates);
      if (error || !data) return null;

      setReminders((prev) => prev.map((r) => (r.id === id ? data : r)));
      return data;
    },
    []
  );

  // Toggle pause
  const togglePause = useCallback(
    async (id: string, isPaused: boolean): Promise<boolean> => {
      const { data, error } = await toggleMedicationReminderPause(id, isPaused);
      if (error || !data) return false;

      setReminders((prev) => prev.map((r) => (r.id === id ? data : r)));
      return true;
    },
    []
  );

  // Remove reminder
  const removeReminder = useCallback(
    async (id: string): Promise<boolean> => {
      const { success } = await deleteMedicationReminder(id);
      if (!success) return false;

      setReminders((prev) => prev.filter((r) => r.id !== id));
      return true;
    },
    []
  );

  // Acknowledge a pending log entry
  const acknowledgeLogEntry = useCallback(
    async (logId: string, wasGiven: boolean, givenAt?: string): Promise<boolean> => {
      const { success } = await acknowledgeReminderLog(logId, wasGiven, givenAt);
      if (!success) return false;

      // Optimistic update: remove from pending
      setPendingLogs((prev) => prev.filter((l) => l.id !== logId));
      return true;
    },
    []
  );

  // Derived state
  const activeReminders = reminders.filter((r) => !r.is_paused);
  const pausedReminders = reminders.filter((r) => r.is_paused);

  return {
    reminders,
    activeReminders,
    pausedReminders,
    pendingLogs,
    isLoading,
    isCreating,
    addReminder,
    editReminder,
    togglePause,
    removeReminder,
    acknowledgeLog: acknowledgeLogEntry,
    refresh: fetchReminders,
  };
}
